import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency } from "@/lib/calc";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Gift, Tag } from "lucide-react";
import { toast } from "sonner";
import { PackageBuilder } from "@/components/packages/PackageBuilder";
import { PlanCard } from "@/components/packages/PlanCard";

const DEFAULT_LABELS = { packages_tab_name: "Bundles & Bribes", packages_bundle_label: "Your Bundle", packages_bribes_label: "Your Bribes" };

export default function Packages() {
  const { business, setBusiness } = useAuth();
  const cur = business?.currency || "AUD";
  const [plans, setPlans] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} (new) | plan
  const [labels, setLabels] = useState(DEFAULT_LABELS);
  const [savingLabels, setSavingLabels] = useState(false);

  const load = () => api.get(`/package-plans?include_archived=${showArchived}`).then(({ data }) => setPlans(data)).catch(() => toast.error("Couldn't load packages"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);
  useEffect(() => { if (business) setLabels({ packages_tab_name: business.packages_tab_name || DEFAULT_LABELS.packages_tab_name, packages_bundle_label: business.packages_bundle_label || DEFAULT_LABELS.packages_bundle_label, packages_bribes_label: business.packages_bribes_label || DEFAULT_LABELS.packages_bribes_label }); }, [business]);

  const flag = async (p, patch, msg) => {
    try { await api.patch(`/package-plans/${p.id}`, patch); load(); if (msg) toast.success(msg); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
  };
  const duplicate = async (p) => {
    try { const { data } = await api.post(`/package-plans/${p.id}/duplicate`); load(); toast.success("Copied — it's inactive until you switch it on"); setEditing(data); }
    catch { toast.error("Couldn't duplicate"); }
  };
  const saveLabels = async () => {
    setSavingLabels(true);
    try { const { data } = await api.put("/business", labels); setBusiness?.(data?.id ? data : { ...business, ...labels }); toast.success(`Clients will see "${labels.packages_tab_name}" on the booking page`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setSavingLabels(false); }
  };

  const live = plans.filter((p) => !p.archived);
  const archived = plans.filter((p) => p.archived);
  return (
    <div data-testid="packages-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Setup · Packages</div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Bundles &amp; Bribes</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">What they buy, what you throw in, what it's worth, what they pay, what they gain — and the rules. Built to sell a treatment plan, not "6 sessions minus 10%".</p>
        </div>
        <Button className="rounded-lg" onClick={() => setEditing({})} data-testid="new-package-button"><Plus className="h-4 w-4 mr-1.5" />Create package</Button>
      </div>

      <Card className="rounded-2xl p-5 mb-6" data-testid="package-labels-card">
        <div className="flex items-center gap-2 mb-3"><Tag className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">What your clients see</h3></div>
        <div className="grid sm:grid-cols-3 gap-3">
          {[["packages_tab_name", "Client-facing tab name", "Shown next to Services on your booking page"], ["packages_bundle_label", "\"Bundle\" heading", "e.g. Your Bundle · Your Treatments"], ["packages_bribes_label", "\"Bribes\" heading", "e.g. Your Bribes · Your Extras"]].map(([k, l, h]) => (
            <div key={k} className="space-y-1">
              <label className="text-xs text-muted-foreground">{l}</label>
              <Input value={labels[k]} onChange={(e) => setLabels((v) => ({ ...v, [k]: e.target.value }))} className="h-10 rounded-lg" data-testid={`label-${k}`} />
              <p className="text-[11px] text-muted-foreground">{h}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
          <p className="text-xs text-muted-foreground">Booking page preview: <span className="font-medium text-foreground">Services | {labels.packages_tab_name || "Bundles & Bribes"}</span></p>
          <Button variant="outline" className="rounded-lg" onClick={saveLabels} disabled={savingLabels} data-testid="save-labels">{savingLabels ? "Saving…" : "Save names"}</Button>
        </div>
      </Card>

      {live.length === 0 && (
        <Card className="rounded-2xl p-10 text-center text-muted-foreground" data-testid="packages-empty">
          <Gift className="h-8 w-8 mx-auto mb-3 text-primary/60" />
          <p className="font-medium text-foreground">No packages yet</p>
          <p className="text-sm mt-1">Create your first — e.g. 6 × Full Body Laser with two products thrown in and a 12-month price lock.</p>
        </Card>
      )}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {live.map((p) => <PlanCard key={p.id} plan={p} cur={cur} labels={labels} onEdit={() => setEditing(p)} onDuplicate={() => duplicate(p)} onFlag={(patch, msg) => flag(p, patch, msg)} />)}
      </div>
      <div className="mt-6 flex items-center gap-3">
        <button type="button" className="text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived">{showArchived ? "Hide archived" : "Show archived"}</button>
        {showArchived && archived.length === 0 && <span className="text-xs text-muted-foreground">Nothing archived.</span>}
      </div>
      {showArchived && archived.length > 0 && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3 opacity-80">
          {archived.map((p) => <PlanCard key={p.id} plan={p} cur={cur} labels={labels} onEdit={() => setEditing(p)} onDuplicate={() => duplicate(p)} onFlag={(patch, msg) => flag(p, patch, msg)} />)}
        </div>
      )}
      {editing !== null && <PackageBuilder plan={editing.id ? editing : null} cur={cur} labels={labels} onClose={(saved) => { setEditing(null); if (saved) load(); }} />}
    </div>
  );
}
